import { ChevronDown, Check, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CampaignType } from "@/constants/campaign-types";
import { campaignTypesInfo } from "./campaign-types-info";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { CampaignTypeInfoContent } from "./campaign-type-info-content";

interface CampaignTypeDropdownSelectorProps {
  value: CampaignType;
  onChange: (type: CampaignType) => void;
  error?: string;
  disabled?: boolean;
}

export function CampaignTypeDropdownSelector({
  value,
  onChange,
  error,
  disabled = false,
}: CampaignTypeDropdownSelectorProps) {
  const { handleModal } = useDialogModal();

  const selectedType = campaignTypesInfo.find((t) => t.id === value);

  const handleSelectType = (type: CampaignType) => {
    const typeInfo = campaignTypesInfo.find((t) => t.id === type);
    if (typeInfo?.enabled) {
      onChange(type);
    }
  };

  const handleShowSelectedInfo = () => {
    if (!selectedType) return;
    handleModal({
      isOpen: true,
      element: (
        <CampaignTypeInfoContent
          technicalCode={selectedType.technicalCode}
          description={selectedType.description}
          objetivo={selectedType.modalInfo.objetivo}
          caracteristicas={selectedType.modalInfo.caracteristicas}
          regras={selectedType.modalInfo.regras}
        />
      ),
      title: selectedType.title,
      description: "",
    });
  };

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-base font-semibold mb-1">
          Tipo de Campanha *
        </h3>
        <p className="text-sm text-muted-foreground">
          Escolha o tipo de promoção que deseja criar
        </p>
      </div>

      <div className="flex gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger className="hover:text-white" asChild>
            <Button
              variant="outline"
              disabled={disabled}
              className={`flex-1 justify-between hover:text-white h-9 px-3 ${
                error ? "border-destructive" : ""
              }`}
            >
              {selectedType ? (
                <div className="flex items-center gap-2 flex-1 text-left hover:text-white">
                  <selectedType.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="font-medium text-sm" >
                    {selectedType.title}
                  </span>
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">Selecione o tipo de campanha</span>
              )}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-[400px] max-w-[90vw] p-1">
            {campaignTypesInfo.filter((type) => type.enabled).map((type) => {
              const Icon = type.icon;
              const isSelected = value === type.id;
              const isDisabled = !type.enabled;

              return (
                <DropdownMenuItem
                  key={type.id}
                  disabled={isDisabled}
                  onClick={() => handleSelectType(type.id)}
                  className={`px-3 py-2 cursor-pointer rounded-sm ${
                    isSelected
                      ? "bg-primary text-primary-foreground [&_svg]:text-primary-foreground [&_span]:text-primary-foreground"
                      : "hover:bg-primary hover:text-primary-foreground [&:hover_svg]:text-primary-foreground [&:hover_span]:text-primary-foreground"
                  } ${isDisabled ? "opacity-60" : ""}`}
                >
                  <div className="flex items-center gap-2 w-full">
                    <Icon className="w-4 h-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium text-sm flex-1">
                      {type.title}
                    </span>
                    {!type.enabled && (
                      <span className="text-xs opacity-70 italic">
                        Em breve
                      </span>
                    )}
                    {isSelected && (
                      <Check className="h-4 w-4 shrink-0" />
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {selectedType && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleShowSelectedInfo}
            className="h-9 w-9 shrink-0"
            title="Mais informações"
          >
            <Info className="w-4 h-4" />
          </Button>
        )}
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
